import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { Form } from "informed";
import { get } from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useReducer, useCallback, useContext } from "react";
import { withRouter } from "react-router-dom";
import { toast } from "react-toastify";
import { CSSTransition } from "react-transition-group";
import { ENROLLMENT_STATUS } from "../../../lib/config";
import { API_URL } from "../../../lib/const";
import Modal from "../../components/ConfirmationModal";
import { InputTypeArea, Spinner } from "../../elements";
import DocumentsDetailReducer from "../../reducers/DocumentsDetailReducer";
import {
  fetchDocuments,
  fetchDocumentTypesWithEquivalents,
  sendDocuments,
} from "../../services/documents_services";
import { fetchPersonalDataSummary } from "../../services/personal_data_service";
import { handleDocumentUpload } from "../../services/upload_service";
import {
  fetchAppropriations,
  createAppropriation,
  transferAppropriation,
} from "../../services/document_appropriation_service";
import {
  fetchDocumentSystemConfig,
  fetchHsTranscriptReasons,
  fetchHsTranscriptCheck,
  saveHsTranscriptCheck,
} from "../../services/hs_transcript_service";
import DocumentsDetailsForm from "../components/DocumentsDetailsForm";
import DocumentsDetailsView from "../components/DocumentsDetailsView";
import AppropriationAlert from "../components/AppropriationAlert";
import { UserContext } from "../../v2/common/states/UserState";

const DocumentsDetailPage = ({ match, history }) => {
  const initialState = {
    loadingPage: false,
    isEditing: false,
    showPendencies: false,
    hasPendencies: false,
    loadingPendencies: false,
    pendencies: [],
    errors: {},
    personalDocumentsTypes: [],
    personalDocuments: [],
    personalData: {},
    form: {},
    pendingDocument: null,
    enrollmentId: 0,
    activeAppropriation: null,
    appropriations: [],
    loadingAppropriation: true,
    hsControlEnabled: false,
    hsReasons: [],
    hsCheckData: {},
    hsSaving: false,
  };
  const { user } = useContext(UserContext);
  const [state, dispatch] = useReducer(DocumentsDetailReducer, initialState);
  const {
    loadingPage,
    personalData,
    loadingPendencies,
    personalDocumentsTypes,
    personalDocuments,
    isEditing,
    confirmationModal,
    showPendencies,
    hasPendencies,
    form,
    pendencies,
    errors,
    pendingDocument,
    enrollmentId,
    activeAppropriation,
    appropriations,
    loadingAppropriation,
    hsControlEnabled,
    hsReasons,
    hsCheckData,
    hsSaving,
  } = state;

  const { token } = match.params;

  const fetchPersonalData = () => {
    fetchPersonalDataSummary(token).then((personalDataSummary) => {
      dispatch({
        type: "SET_PERSONAL_DATA",
        payload: personalDataSummary,
      });
    });
  };

  const fetchPersonalDocuments = () => {
    fetchDocuments(token).then((res) =>
      dispatch({ type: "SET_PERSONAL_DOCUMENTS", payload: res.documents })
    );
  };

  const fetchPendencies = (id) => {
    const dadosPessoais = "Dados pessoais";
    dispatch({ type: "SET_LOADING_PENDENCIES" });
    axios
      .get(`${API_URL}/onboarding/pendencies`, {
        params: {
          enrollment: id,
        },
      })
      .then((response) => {
        dispatch({ type: "SET_LOADING_PENDENCIES" });
        const activePendencies = response.data.filter((f) => !f.resolved_at);
        dispatch({
          type: "SET_PENDENCIES_LIST",
          payload: response.data.filter((p) => p.step === dadosPessoais),
        });
        if (activePendencies.length > 0) {
          dispatch({ type: "SET_PENDENCIES_LIST_VISIBILITY", payload: true });
        }
      })
      .catch(() => {
        dispatch({ type: "SET_LOADING_PENDENCIES" });
      });
  };

  const fetchAppropriationData = useCallback(() => {
    if (!enrollmentId) return;

    dispatch({ type: "SET_LOADING_APPROPRIATION", payload: true });

    fetchAppropriations(enrollmentId)
      .then((appropriations) => {
        const active = (appropriations || []).find((a) => !a.end_date) || null;
        dispatch({
          type: "SET_ACTIVE_APPROPRIATION",
          payload: active,
        });

        dispatch({
          type: "SET_APPROPRIATIONS",
          payload: appropriations || [],
        });
      })
      .catch(() => {
        dispatch({ type: "SET_LOADING_APPROPRIATION", payload: false });
      });

  }, [enrollmentId]);

  const fetchHsData = useCallback(async (enrollToken) => {
    try {
      const config = await fetchDocumentSystemConfig();
      dispatch({ type: "SET_HS_CONTROL_ENABLED", payload: config.hs_transcript_control_enabled });
    } catch (e) {
      console.error("[HE] Erro ao carregar config:", e);
    }
    try {
      const reasons = await fetchHsTranscriptReasons();
      dispatch({ type: "SET_HS_REASONS", payload: reasons });
    } catch (e) {
      console.error("[HE] Erro ao carregar motivos:", e);
    }
    try {
      const check = await fetchHsTranscriptCheck(enrollToken);
      dispatch({ type: "SET_HS_CHECK_DATA", payload: check });
    } catch (e) {
      console.error("[HE] Erro ao carregar check:", e);
    }
  }, []);

  const handleHsCheckChange = (newCheckData) => {
    dispatch({ type: "SET_HS_CHECK_DATA", payload: newCheckData });
  };

  const handleAppropriate = useCallback(async () => {
    dispatch({ type: "SET_LOADING_APPROPRIATION", payload: true });

    try {
      await createAppropriation(enrollmentId);
      toast.success("Apropriação criada com sucesso!");
      fetchAppropriationData();
    } catch (error) {
      toast.error("Ocorreu um erro ao criar apropriação. Tente novamente.");
      dispatch({ type: "SET_LOADING_APPROPRIATION", payload: false });
    }
  }, [enrollmentId]);

  const handleTransferAppropriation = useCallback(
    async (appropriationId, userId) => {
      dispatch({ type: "SET_LOADING_APPROPRIATION", payload: true });

      try {
        await transferAppropriation(appropriationId, userId);
        toast.success("Apropriação transferida com sucesso!");
        fetchAppropriationData();
      } catch (error) {
        const detail = get(error, "response.data.non_field_errors[0]");
        toast.error(
          detail || "Ocorreu um erro ao transferir a apropriação. Tente novamente."
        );
        dispatch({ type: "SET_LOADING_APPROPRIATION", payload: false });
      }
    },
    [fetchAppropriationData]
  );

  const fetchData = () => {
    dispatch({ type: "SET_LOADING_PAGE", payload: true });
    axios
      .all([
        fetchPersonalDataSummary(token),
        fetchDocumentTypesWithEquivalents(token),
        fetchDocuments(token),
      ])
      .then(([personalDataSummary, documentsTypes, documents]) => {
        fetchPendencies(personalDataSummary.enrollment.id);
        const enrollmentIdValue = personalDataSummary.enrollment.id;
        dispatch({
          type: "SET_ENROLLMENT_ID",
          payload: enrollmentIdValue,
        });
        dispatch({
          type: "SET_PERSONAL_DATA",
          payload: personalDataSummary,
        });
        dispatch({
          type: "SET_PERSONAL_DOCUMENTS_TYPES",
          payload: documentsTypes,
        });
        dispatch({
          type: "SET_PERSONAL_DOCUMENTS",
          payload: documents.documents,
        });
      });
  };

  const sendPendencies = () => {
    dispatch({ type: "SET_LOADING_PENDENCIES" });
    axios
      .post(`${API_URL}/onboarding/enrollments/${token}/pendency`)
      .then(() => {
        fetchData();
        fetchAppropriationData();
        toast.success("Pendências enviadas com sucesso!");
        dispatch({ type: "SET_LOADING_PENDENCIES" });
        dispatch({ type: "SET_PENDENCIES_LIST_VISIBILITY", payload: false });
      })
      .catch(() => {
        toast.error(
          "Ocorreu um erro ao enviar a(s) pendência(s). Por favor tente novamente."
        );
        dispatch({ type: "SET_LOADING_PENDENCIES" });
      });
  };

  const handleUpload = async (event, document_type) => {
    const file = get(event, "target.files.0");
    try {
      const response = await handleDocumentUpload(file, document_type);
      const { key } = response;
      const newPersonalDocument = { key, document_type, name: file.name };
      dispatch({
        type: "ADD_PERSONAL_DOCUMENTS",
        payload: newPersonalDocument,
      });
    } catch (error) {
      toast.error("Ocorreu um erro ao enviar arquivo. Tente novamente.");
    }
  };

  const handleRemoveDocument = (key) => {
    const newListDocuments = personalDocuments.filter((d) => d.key !== key);
    dispatch({
      type: "REMOVE_PERSONAL_DOCUMENTS",
      payload: newListDocuments,
    });
  };

  const handlePendency = async () => {
    const dadosPessoais = 2;

    if (pendingDocument) {
      await axios
        .patch(`${API_URL}/documents/documents-data/${token}/review_document`, {
          document_id: pendingDocument.id,
          approved: false,
        })
        .then(() => {
          toast.success("Documento reprovado!");
          fetchPersonalDocuments(token);
        })
        .catch((e) => {
          console.error(e);
          return toast.error("Algo deu errado.");
        });
    }

    await axios
      .post(`${API_URL}/onboarding/pendencies`, {
        ...form,
        step: dadosPessoais,
        enrollment: personalData.enrollment.id,
      })
      .then(() => {
        dispatch({ type: "SET_PENDENCIES_LIST_VISIBILITY", payload: false });
        dispatch({ type: "CLOSE_PENDENCIES_VISIBILITY" });
        fetchData();
      })
      .catch(() => {
        toast.error(
          "Ocorreu um erro ao criar pendência. Por favor tente novamente mais tarde"
        );
      });
  };

  const handleDocumentApproval = async (doc) => {
    if (!window.confirm("Tem certeza de que deseja aprovar o documento"))
      return;

    await axios
      .patch(`${API_URL}/documents/documents-data/${token}/review_document`, {
        document_id: doc.id,
        approved: true,
      })
      .then(() => {
        toast.success("Documento aprovado!");
        fetchPersonalDocuments(token);
      })
      .catch((e) => {
        console.error(e);
        return toast.error("Algo deu errado.");
      });
  };

  const handleDocumentReproval = async (p_document) => {
    dispatch({ type: "OPEN_PENDENCIES_VISIBILITY", payload: p_document });
  };

  const getPersonalDocuments = (document_type) =>
    personalDocuments.filter((d) => d.document_type === document_type);

  const handleSavePersonal = async () => {
    dispatch({ type: "SET_LOADING_PAGE", payload: true });
    try {
      await sendDocuments(token, { documents: personalDocuments });
      window.scroll(0, 0);
      dispatch({ type: "SET_EDITING" });
      fetchPersonalData();
      fetchPersonalDocuments();
    } catch (err) {
      toast.error("Ocorreu um erro ao editar o formulário.");
      dispatch({
        type: "SET_DOCUMENTS_ERROR",
        payload: get(err, "response.data"),
      });
    }
    dispatch({ type: "SET_LOADING_PAGE", payload: false });
  };

  const isHsCheckComplete = () => {
    if (!hsControlEnabled) return true;
    if (hsCheckData.has_transcript === true) return true;
    if (hsCheckData.has_transcript === false) {
      return !!(hsCheckData.reason || (hsCheckData.other_reason || '').trim());
    }
    return false;
  };

  const handleApproveData = async () => {
    if (hsControlEnabled) {
      dispatch({ type: "SET_HS_SAVING", payload: true });
      try {
        const saved = await saveHsTranscriptCheck(token, {
          has_transcript: hsCheckData.has_transcript,
          reason: hsCheckData.reason || null,
          other_reason: hsCheckData.other_reason || '',
        });
        dispatch({ type: "SET_HS_CHECK_DATA", payload: saved });
      } catch (_) {
        toast.error("Erro ao salvar análise do Histórico Escolar.");
        dispatch({ type: "SET_HS_SAVING", payload: false });
        return;
      }
      dispatch({ type: "SET_HS_SAVING", payload: false });
    }

    await axios.post(
      `${API_URL}/documents/documents-data/${token}/approve_data`
    );
    toast.success("Aluno aprovado com sucesso!");
    dispatch({ type: "SET_CONFIRMATION_MODAL" });
    fetchData();
    fetchAppropriationData();
  };

  const handleModal = () => {
    dispatch({ type: "SET_CONFIRMATION_MODAL" });
  };

  const handleEdit = () => {
    dispatch({ type: "SET_EDITING" });
  };

  const canApprove = () => {
    const document_status = get(personalData, "enrollment.document_status", {});
    const status = get(personalData, "enrollment.status", {});
    return (
      get(document_status, "id") === 3 &&
      [
        ENROLLMENT_STATUS.PAYMENT_REVIEWING,
        ENROLLMENT_STATUS.REVIEWING,
        ENROLLMENT_STATUS.WAITING_PAYMENT,
      ].includes(status.id)
    );
  };

  const isAppropriatedByMe = !!(
    activeAppropriation &&
    user &&
    activeAppropriation.user.id === user.id
  );

  // A transferência é do usuário apropriado, mas o superusuário também pode
  // executá-la (mesma regra validada na API).
  const canTransferAppropriation = !!(
    isAppropriatedByMe || (user && user.is_superuser)
  );

  useEffect(() => {
    fetchData();
    fetchHsData(token);
  }, []);

  useEffect(() => {
    if (enrollmentId) {
      fetchAppropriationData();
    }
  }, [enrollmentId]);

  return (
    <div className="container">
      <Spinner active={loadingPage}>
        <div className="one-page-only mb-4">
          {(!loadingAppropriation && (canApprove() || get(personalData, "enrollment.status.id") === ENROLLMENT_STATUS.PENDING)) && (
            <AppropriationAlert
              activeAppropriation={activeAppropriation}
              appropriations={appropriations}
              onAppropriate={handleAppropriate}
              loading={loadingAppropriation}
            />
          )}
          {!isEditing &&
            canApprove() && (
              <>
                <button
                  type="button"
                  className="float-button mr-6"
                  onClick={() => handleEdit()}
                >
                  <FontAwesomeIcon
                    spin={loadingPage}
                    icon={loadingPage ? "spinner" : "edit"}
                  />
                </button>
                <button
                  type="button"
                  className="float-button"
                  onClick={() =>
                    dispatch({ type: "OPEN_PENDENCIES_VISIBILITY" })
                  }
                >
                  <FontAwesomeIcon
                    spin={loadingPage}
                    icon={loadingPage ? "spinner" : "plus"}
                  />
                </button>
              </>
            )}
          {isEditing ? (
            <DocumentsDetailsForm
              onSavePersonal={handleSavePersonal}
              personalDocumentsTypes={personalDocumentsTypes}
              onUpload={handleUpload}
              onRemoveDocument={handleRemoveDocument}
              getPersonalDocuments={getPersonalDocuments}
              onEdit={handleEdit}
              errors={errors}
            />
          ) : (
            <DocumentsDetailsView
              reloadPendencies={fetchPendencies}
              enrollmentId={enrollmentId}
              onApproveDocument={handleDocumentApproval}
              onReproveDocument={handleDocumentReproval}
              personalData={personalData}
              personalDocumentsTypes={personalDocumentsTypes}
              personalDocuments={personalDocuments}
              pendencies={pendencies}
              loadingPendencies={loadingPendencies}
              appropriations={appropriations}
              canTransferAppropriation={canTransferAppropriation}
              onTransferAppropriation={handleTransferAppropriation}
              loadingAppropriation={loadingAppropriation}
              canAuditDocuments={isAppropriatedByMe}
              hsControlEnabled={hsControlEnabled && canApprove() && isAppropriatedByMe}
              hsCheckData={hsCheckData}
              hsReasons={hsReasons}
              onHsCheckChange={handleHsCheckChange}
              hsSaving={hsSaving}
            />
          )}
          <div className="btn-actions offset-md-8 mb-4 col-md-4 mt-4 p-0 col-sm-12">
            <button
              type="button"
              className="btn btn-black mr-2"
              onClick={() => history.push("/academic")}
            >
              Voltar
            </button>
            {(pendencies.filter((f) => !f.resolved_at).length > 0 ||
              hasPendencies) && (
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={
                    get(personalData, "enrollment.status.id") === 3 ||
                    loadingPendencies
                  }
                  onClick={sendPendencies}
                >
                  Enviar todas pendências
                </button>
              )}
            {canApprove() &&
              !hasPendencies &&
              isAppropriatedByMe && (
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={isEditing || !isHsCheckComplete() || hsSaving}
                  onClick={() => handleModal()}
                  title={!isHsCheckComplete() ? "Preencha a análise do Histórico Escolar do Ensino Médio antes de aprovar" : undefined}
                >
                  {isEditing ? "Editando" : "Aprovar"}
                </button>
              )}
          </div>
        </div>
        <Modal
          visible={confirmationModal}
          title="Confirmação"
          onCancel={() => dispatch({ type: "SET_CONFIRMATION_MODAL" })}
          onConfirm={handleApproveData}
        >
          Você tem certeza que deseja aprovar este aluno?
        </Modal>
        <CSSTransition
          in={showPendencies}
          timeout={300}
          classNames="expand-animation"
          unmountOnExit
        >
          <aside className="financer-float">
            <button
              className="btn-none float-right"
              type="button"
              onClick={() => dispatch({ type: "CLOSE_PENDENCIES_VISIBILITY" })}
            >
              <FontAwesomeIcon icon="times" />
            </button>
            <h4 className="mt-3 bt-3">Pendências</h4>
            <hr />
            <Form
              id="pendencias"
              onValueChange={(values) =>
                dispatch({ type: "SET_FORM", payload: values })
              }
            >
              <InputTypeArea
                field="description"
                initialValue={
                  pendingDocument
                    ? `Pendência para documento: ${pendingDocument.document_type
                    } \n`
                    : ""
                }
                label="Descrição"
                row="10"
              />
              <button
                type="submit"
                className="btn btn-primary"
                onClick={() => handlePendency()}
              >
                Criar Pendência
              </button>
            </Form>
          </aside>
        </CSSTransition>
      </Spinner>
    </div>
  );
};

DocumentsDetailPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      token: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.string,
  }).isRequired,
};

export default withRouter(DocumentsDetailPage);
